import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import {
  Printer, PenTool, Car, UtensilsCrossed, Package, UserCheck, Lock,
  Loader2, Clock, CheckCircle2, XCircle, AlertCircle, Plus, Edit, Trash2,
  Settings, ListOrdered
} from 'lucide-react';

const CATEGORY_CONFIG = {
  stationery: { label: 'Stationery & Print Design', icon: PenTool, color: 'text-violet-500', bg: 'bg-violet-500/10', border: 'border-violet-500/20' },
  printing: { label: 'Printing & Scanning', icon: Printer, color: 'text-blue-500', bg: 'bg-blue-500/10', border: 'border-blue-500/20' },
  parking: { label: 'Parking', icon: Car, color: 'text-amber-500', bg: 'bg-amber-500/10', border: 'border-amber-500/20' },
  food_beverage: { label: 'Food & Beverage', icon: UtensilsCrossed, color: 'text-orange-500', bg: 'bg-orange-500/10', border: 'border-orange-500/20' },
  courier: { label: 'Courier & Dispatch', icon: Package, color: 'text-emerald-500', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
  visitor: { label: 'Visitor Management', icon: UserCheck, color: 'text-cyan-500', bg: 'bg-cyan-500/10', border: 'border-cyan-500/20' },
  storage: { label: 'Storage & Lockers', icon: Lock, color: 'text-rose-500', bg: 'bg-rose-500/10', border: 'border-rose-500/20' },
};

const STATUS_CONFIG = {
  pending: { label: 'Pending', icon: Clock, color: 'bg-amber-500/20 text-amber-600 border-amber-500/30' },
  in_progress: { label: 'In Progress', icon: AlertCircle, color: 'bg-blue-500/20 text-blue-600 border-blue-500/30' },
  completed: { label: 'Completed', icon: CheckCircle2, color: 'bg-emerald-500/20 text-emerald-600 border-emerald-500/30' },
  cancelled: { label: 'Cancelled', icon: XCircle, color: 'bg-red-500/20 text-red-600 border-red-500/30' },
  rejected: { label: 'Rejected', icon: XCircle, color: 'bg-red-500/20 text-red-600 border-red-500/30' },
};

const EMPTY_FORM = { name: '', category: 'stationery', description: '', base_price: 0, unit: '', is_active: true };

export const ServiceRequestsTab = ({ api, theme }) => {
  const { cardBg, cardBorder } = theme;
  const [view, setView] = useState('requests'); // requests | catalog
  const [services, setServices] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [catalogFilter, setCatalogFilter] = useState('all');

  // Service form state
  const [showForm, setShowForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [sRes, rRes] = await Promise.all([
        api.get('/services/catalog'),
        api.get('/services/requests'),
      ]);
      setServices(sRes.data);
      setRequests(rRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // --- Catalog CRUD ---
  const openAdd = () => {
    setEditingService(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (s) => {
    setEditingService(s);
    setForm({
      name: s.name,
      category: s.category,
      description: s.description || '',
      base_price: s.base_price,
      unit: s.unit || '',
      is_active: s.is_active,
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Service name is required'); return; }
    setSaving(true);
    try {
      if (editingService) {
        await api.put(`/services/catalog/${editingService.id}`, form);
        toast.success('Service updated');
      } else {
        await api.post('/services/catalog', form);
        toast.success('Service created');
      }
      setShowForm(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (serviceId) => {
    try {
      await api.delete(`/services/catalog/${serviceId}`);
      toast.success('Service deleted');
      setDeleteConfirm(null);
      fetchAll();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleToggleActive = async (s) => {
    try {
      await api.put(`/services/catalog/${s.id}`, { is_active: !s.is_active });
      toast.success(`Service ${!s.is_active ? 'enabled' : 'disabled'}`);
      fetchAll();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  // --- Request Actions ---
  const updateStatus = async (requestId, newStatus) => {
    try {
      await api.put(`/services/requests/${requestId}/status`, { status: newStatus });
      toast.success(`Request marked as ${newStatus.replace('_', ' ')}`);
      fetchAll();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  const filteredRequests = filter === 'all' ? requests : requests.filter(r => r.status === filter);
  const filteredServices = catalogFilter === 'all' ? services : services.filter(s => s.category === catalogFilter);

  const reqCounts = {
    all: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    in_progress: requests.filter(r => r.status === 'in_progress').length,
    completed: requests.filter(r => r.status === 'completed').length,
  };

  return (
    <div className="space-y-4" data-testid="service-requests-tab">
      {/* View Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setView('requests')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              view === 'requests' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="view-requests-btn"
          >
            <ListOrdered className="w-4 h-4" /> Requests
            {reqCounts.pending > 0 && (
              <span className="w-5 h-5 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center">{reqCounts.pending}</span>
            )}
          </button>
          <button
            onClick={() => setView('catalog')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              view === 'catalog' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="view-catalog-btn"
          >
            <Settings className="w-4 h-4" /> Manage Catalog ({services.length})
          </button>
        </div>
        {view === 'catalog' && (
          <Button size="sm" onClick={openAdd} data-testid="add-service-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Service
          </Button>
        )}
      </div>

      {view === 'requests' ? (
        /* ========= REQUESTS VIEW ========= */
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            {Object.entries(reqCounts).map(([key, count]) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  filter === key ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {key === 'all' ? 'All' : key === 'in_progress' ? 'In Progress' : key.charAt(0).toUpperCase() + key.slice(1)}
                <span className="ml-1 opacity-70">({count})</span>
              </button>
            ))}
          </div>

          {filteredRequests.length === 0 ? (
            <Card className={`${cardBg} ${cardBorder}`}>
              <CardContent className="p-12 text-center">
                <Package className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No service requests {filter !== 'all' ? `with status "${filter}"` : 'yet'}</p>
              </CardContent>
            </Card>
          ) : (
            filteredRequests.map(req => {
              const sc = STATUS_CONFIG[req.status] || STATUS_CONFIG.pending;
              const catConfig = CATEGORY_CONFIG[req.service_category] || CATEGORY_CONFIG.stationery;
              const CatIcon = catConfig.icon;
              const StatusIcon = sc.icon;
              const created = new Date(req.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

              return (
                <Card key={req.id} className={`${cardBg} ${cardBorder}`} data-testid={`admin-request-${req.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className={`w-9 h-9 rounded-lg ${catConfig.bg} flex items-center justify-center shrink-0`}>
                          <CatIcon className={`w-4 h-4 ${catConfig.color}`} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm truncate">{req.service_name}</p>
                            <Badge className={`${sc.color} border text-[10px] shrink-0`}>
                              <StatusIcon className="w-3 h-3 mr-1" />{sc.label}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {req.user_name} ({req.user_email}) &middot; {req.centre_name} &middot; Qty: {req.quantity} &middot; {created}
                          </p>
                          {req.notes && <p className="text-xs text-muted-foreground/70 mt-0.5 italic">"{req.notes}"</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        {req.estimated_amount > 0 && (
                          <p className="text-sm font-semibold whitespace-nowrap">₹{req.estimated_amount}</p>
                        )}
                        {req.status === 'pending' && (
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => updateStatus(req.id, 'in_progress')}>Accept</Button>
                            <Button size="sm" variant="ghost" className="h-7 text-xs text-destructive hover:text-destructive" onClick={() => updateStatus(req.id, 'rejected')}>Reject</Button>
                          </div>
                        )}
                        {req.status === 'in_progress' && (
                          <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => updateStatus(req.id, 'completed')}>Complete</Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      ) : (
        /* ========= CATALOG VIEW ========= */
        <div className="space-y-3">
          {/* Category Filter */}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setCatalogFilter('all')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                catalogFilter === 'all' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              All ({services.length})
            </button>
            {Object.entries(CATEGORY_CONFIG).map(([key, config]) => {
              const count = services.filter(s => s.category === key).length;
              if (count === 0) return null;
              return (
                <button
                  key={key}
                  onClick={() => setCatalogFilter(key)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    catalogFilter === key ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {config.label} ({count})
                </button>
              );
            })}
          </div>

          {/* Services Table */}
          <Card className={`${cardBg} ${cardBorder} overflow-hidden`}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={`border-b ${cardBorder} text-left`}>
                    <th className="px-4 py-3 font-medium text-muted-foreground text-xs">Service</th>
                    <th className="px-4 py-3 font-medium text-muted-foreground text-xs">Category</th>
                    <th className="px-4 py-3 font-medium text-muted-foreground text-xs">Price</th>
                    <th className="px-4 py-3 font-medium text-muted-foreground text-xs">Unit</th>
                    <th className="px-4 py-3 font-medium text-muted-foreground text-xs">Status</th>
                    <th className="px-4 py-3 font-medium text-muted-foreground text-xs text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredServices.map(s => {
                    const catConfig = CATEGORY_CONFIG[s.category] || CATEGORY_CONFIG.stationery;
                    const CatIcon = catConfig.icon;
                    return (
                      <tr key={s.id} className={`border-b ${cardBorder} last:border-0 hover:bg-muted/5 transition-colors`} data-testid={`catalog-row-${s.id}`}>
                        <td className="px-4 py-3">
                          <p className="font-medium">{s.name}</p>
                          {s.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{s.description}</p>}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <CatIcon className={`w-3.5 h-3.5 ${catConfig.color}`} />
                            <span className="text-xs">{catConfig.label}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 font-semibold">
                          {s.base_price > 0 ? `₹${s.base_price}` : <span className="text-emerald-500">Free</span>}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground text-xs">{s.unit || '-'}</td>
                        <td className="px-4 py-3">
                          <Switch
                            checked={s.is_active}
                            onCheckedChange={() => handleToggleActive(s)}
                            data-testid={`toggle-active-${s.id}`}
                          />
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(s)} data-testid={`edit-service-${s.id}`}>
                              <Edit className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 hover:bg-destructive/20 hover:text-destructive" onClick={() => setDeleteConfirm(s)} data-testid={`delete-service-${s.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* Add/Edit Service Modal */}
      <Dialog open={showForm} onOpenChange={(open) => { if (!open) setShowForm(false); }}>
        <DialogContent className={cardBg}>
          <DialogHeader>
            <DialogTitle className="font-heading">{editingService ? 'Edit Service' : 'Add New Service'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Service Name *</Label>
              <Input
                placeholder="e.g., Business Card Printing"
                value={form.name}
                onChange={(e) => setForm(f => ({...f, name: e.target.value}))}
                data-testid="service-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select value={form.category} onValueChange={(v) => setForm(f => ({...f, category: v}))}>
                  <SelectTrigger data-testid="service-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Unit</Label>
                <Input
                  placeholder="e.g., per page, per card"
                  value={form.unit}
                  onChange={(e) => setForm(f => ({...f, unit: e.target.value}))}
                  data-testid="service-unit-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Base Price (₹)</Label>
              <Input
                type="number"
                min={0}
                step={0.01}
                value={form.base_price}
                onChange={(e) => setForm(f => ({...f, base_price: parseFloat(e.target.value) || 0}))}
                data-testid="service-price-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                placeholder="Brief description of this service..."
                value={form.description}
                onChange={(e) => setForm(f => ({...f, description: e.target.value}))}
                data-testid="service-description-input"
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch
                checked={form.is_active}
                onCheckedChange={(v) => setForm(f => ({...f, is_active: v}))}
                data-testid="service-active-toggle"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-service-btn">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {editingService ? 'Update Service' : 'Create Service'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={!!deleteConfirm} onOpenChange={(open) => { if (!open) setDeleteConfirm(null); }}>
        <DialogContent className={cardBg}>
          <DialogHeader>
            <DialogTitle className="font-heading">Delete Service</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground py-2">
            Are you sure you want to delete <strong>{deleteConfirm?.name}</strong>? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm.id)} data-testid="confirm-delete-btn">
              Delete Service
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
